// Dependency-free, so `node --test` can load it: no Sentry, no PostHog.

import type { ClientContext } from '@cineamo/api/models/cart/ClientContext.types';

/** The id of a PaymentIntent from its client secret (`pi_123_secret_456`); the secret part is never reported. */
export function paymentIntentIdOf(clientSecret: string): string {
    return clientSecret.split('_secret_')[0];
}

export type ErrorLike = {
    status?: number;
    requestId?: string;
    message?: string;
    data?: { title?: string; detail?: string; reason_code?: string } | null;
};

/** The bits of a failure worth reporting. Never the whole response: it can carry what the buyer typed. */
export function describeBookingError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) return { name: error.name, message: error.message };
    if (typeof error !== 'object' || error === null) return error === undefined ? {} : { value: String(error) };
    const { status, requestId, message, data } = error as ErrorLike;
    const described = {
        status,
        request_id: requestId,
        message,
        title: data?.title,
        detail: data?.detail,
        reason_code: data?.reason_code
    };
    return Object.fromEntries(Object.entries(described).filter(([, value]) => value !== undefined));
}

export type ClientContextInput = {
    /** The app's `Client` value of the booking ('web', 'cinema_website', 'ios', ...). */
    client?: string;
    pathname?: string;
    userAgent?: string;
    release?: string;
    requestId?: string;
    /** The visitor's analysis consent: the replay ids are left out without it (§ 25 TDDDG). */
    consent: boolean;
    posthogSessionId?: string;
    sentryReplayId?: string;
};

/**
 * The context the booking sends with the PaymentIntent request and the seat-lock PUT (CIN-15436). The replay ids are
 * added only under consent. The user agent is ASCII-only and capped: it travels in a header too, where a non-Latin-1
 * character makes `fetch` throw.
 */
export function buildClientContext(input: ClientContextInput): ClientContext {
    let client: ClientContext['client'];
    if (input.pathname?.includes('/booking/embedded')) client = 'embedded';
    else if (input.client === 'web') client = 'web';
    else if (input.client === 'cinema_website') client = 'cinema-website';
    else if (input.client === 'ios' || input.client === 'android') client = 'app';

    const context: ClientContext = {
        client,
        release: input.release,
        userAgent: input.userAgent?.replace(/[^\x20-\x7e]/g, '').slice(0, 200) || undefined,
        requestId: input.requestId,
        ...(input.consent ? { posthogSessionId: input.posthogSessionId, sentryReplayId: input.sentryReplayId } : {})
    };
    return Object.fromEntries(Object.entries(context).filter(([, value]) => value)) as ClientContext;
}
